import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, ChevronRight, ArrowLeft, FileText, Check, Users, Briefcase, Building, FolderOpen } from 'lucide-react';
import { useCourtierLight } from '../../hooks/useCourtierLight';

const SRC = [
  { id: 'clients', labelKey: 'mailSrcClients', icon: Users },
  { id: 'courtiers', labelKey: 'mailSrcCourtiers', icon: Briefcase },
  { id: 'companies', labelKey: 'mailSrcCompanies', icon: Building },
  { id: 'cabinet', labelKey: 'mailSrcCabinet', icon: FolderOpen },
];

const entityName = (e) => {
  if (e.first_name || e.last_name) return `${e.first_name || ''} ${e.last_name || ''}`.trim();
  return e.name || e.company || e.company_name || e.email || '—';
};
const docName = (d) => d.original_filename || d.filename || d.name || 'document';

/**
 * Sélecteur de documents CRM mobile — mêmes endpoints que CRMAttachmentPicker desktop.
 * Sélection multiple, puis téléchargement + jointure gérés par le parent (onConfirm).
 */
const MobileCRMDocPicker = ({ backendUrl, authToken, onConfirm, onClose }) => {
  const { t } = useTranslation('admin');
  const isCourtierLight = useCourtierLight();
  const sources = isCourtierLight ? SRC.filter((s) => s.id !== 'courtiers') : SRC;
  const H = { Authorization: `Bearer ${authToken}` };

  const [source, setSource] = useState('clients');
  const [entities, setEntities] = useState([]);
  const [entity, setEntity] = useState(null);
  const [docs, setDocs] = useState([]);
  const [entityQuery, setEntityQuery] = useState('');
  const [docQuery, setDocQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]); // {key,id,source,entityId,name,contentType}

  const loadEntities = useCallback(async (src, q = '') => {
    setLoading(true); setEntity(null); setDocs([]);
    try {
      let url = '';
      if (src === 'clients') url = `${backendUrl}/api/admin/users?role=client&limit=30&page=1${q ? `&search=${encodeURIComponent(q)}` : ''}`;
      else if (src === 'courtiers') url = `${backendUrl}/api/admin/courtiers`;
      else if (src === 'companies') url = `${backendUrl}/api/admin/contact-directory/companies`;
      else if (src === 'cabinet') url = `${backendUrl}/api/admin/cabinet/documents`;
      const res = await fetch(url, { headers: H });
      if (res.ok) {
        const data = await res.json();
        if (src === 'clients') setEntities(data.users || data.items || data || []);
        else if (src === 'courtiers') setEntities(data.items || data || []);
        else if (src === 'companies') setEntities(data || []);
        else if (src === 'cabinet') { setDocs(data || []); setEntity({ id: 'cabinet' }); }
      }
    } catch (e) { /* */ } finally { setLoading(false); }
  }, [backendUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadEntities(source); }, [source, loadEntities]);

  const loadDocs = async (ent) => {
    setEntity(ent); setLoading(true); setDocs([]); setDocQuery('');
    try {
      let url = '';
      if (source === 'clients') url = `${backendUrl}/api/client/${ent.id}/documents`;
      else if (source === 'courtiers') url = `${backendUrl}/api/courtier/${ent.id}/documents`;
      else if (source === 'companies') url = `${backendUrl}/api/admin/contact-directory/companies/${ent.id}/documents`;
      const res = await fetch(url, { headers: H });
      if (res.ok) setDocs(await res.json() || []);
    } catch (e) { /* */ } finally { setLoading(false); }
  };

  const toggleDoc = (doc) => {
    const key = `${source}-${doc.id}`;
    setSelected((prev) => prev.some((d) => d.key === key)
      ? prev.filter((d) => d.key !== key)
      : [...prev, { key, id: doc.id, source, entityId: entity?.id, name: docName(doc), contentType: doc.content_type || doc.mime_type }]);
  };

  const filteredEntities = entities.filter((e) => !entityQuery || entityName(e).toLowerCase().includes(entityQuery.toLowerCase()) || (e.email || '').toLowerCase().includes(entityQuery.toLowerCase()));
  const filteredDocs = docs.filter((d) => !docQuery || docName(d).toLowerCase().includes(docQuery.toLowerCase()));
  const showEntityList = source !== 'cabinet' && !entity;

  return (
    <div className="fixed inset-0 z-[110] bg-app-bg flex flex-col" data-testid="mobile-crm-doc-picker">
      <header className="h-14 px-2 flex items-center gap-2 border-b border-line bg-surface shrink-0">
        {entity && source !== 'cabinet' ? (
          <button onClick={() => { setEntity(null); setDocs([]); }} data-testid="docpicker-back" className="h-10 w-10 rounded-full flex items-center justify-center text-ink-secondary active:scale-95"><ArrowLeft size={20} /></button>
        ) : (
          <button onClick={onClose} data-testid="docpicker-close" className="h-10 w-10 rounded-full flex items-center justify-center text-ink-secondary active:scale-95"><X size={20} /></button>
        )}
        <h2 className="flex-1 font-heading font-bold text-ink truncate">{entity && source !== 'cabinet' ? entityName(entity) : t('mobile.mailAttachTitle')}</h2>
        <button onClick={() => { onConfirm(selected); onClose(); }} disabled={selected.length === 0} data-testid="docpicker-confirm" className="h-10 px-4 rounded-full bg-brand-primary text-white font-semibold text-sm disabled:opacity-50 active:scale-95">
          {t('mobile.mailAttachAction')}{selected.length > 0 ? ` (${selected.length})` : ''}
        </button>
      </header>

      {/* Sources */}
      <div className="flex gap-2 px-3 py-2 overflow-x-auto shrink-0 border-b border-line">
        {sources.map((s) => {
          const Icon = s.icon; const on = source === s.id;
          return (
            <button key={s.id} onClick={() => setSource(s.id)} data-testid={`docpicker-src-${s.id}`} className={`flex items-center gap-1.5 h-9 px-3 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${on ? 'bg-brand-primary text-white' : 'bg-surface ring-1 ring-line/70 text-ink-secondary'}`}>
              <Icon size={15} /> {t(`mobile.${s.labelKey}`)}
            </button>
          );
        })}
      </div>

      {/* Recherche */}
      <div className="px-3 pt-3 shrink-0">
        <div className="flex items-center gap-2 bg-surface rounded-full ring-1 ring-line/70 px-3 h-11">
          <Search size={18} className="text-ink-muted" />
          <input
            value={showEntityList ? entityQuery : docQuery}
            onChange={(e) => (showEntityList ? setEntityQuery(e.target.value) : setDocQuery(e.target.value))}
            data-testid="docpicker-search"
            placeholder={showEntityList ? t('mobile.mailSearchEntity') : t('mobile.mailSearchDoc')}
            className="flex-1 bg-transparent outline-none text-sm text-ink"
          />
        </div>
      </div>

      {/* Contenu */}
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {loading ? (
          <div className="pt-16 flex justify-center"><div className="w-7 h-7 border-4 border-brand-primary/20 border-t-brand-primary rounded-full animate-spin" /></div>
        ) : showEntityList ? (
          filteredEntities.length === 0 ? <p className="text-center text-ink-muted text-sm pt-16">{t('mobile.mailNoResult')}</p> : (
            <ul className="space-y-1.5">
              {filteredEntities.map((e) => (
                <li key={e.id}>
                  <button onClick={() => loadDocs(e)} data-testid={`docpicker-entity-${e.id}`} className="w-full flex items-center gap-3 bg-surface rounded-2xl ring-1 ring-line/70 px-4 py-3 text-left active:bg-surface-subtle">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-ink truncate">{entityName(e)}</p>
                      {e.email && <p className="text-xs text-ink-muted truncate">{e.email}</p>}
                    </div>
                    <ChevronRight size={18} className="text-ink-muted shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : (
          filteredDocs.length === 0 ? <p className="text-center text-ink-muted text-sm pt-16">{t('mobile.mailNoDoc')}</p> : (
            <ul className="space-y-1.5">
              {filteredDocs.map((d) => {
                const sel = selected.some((x) => x.key === `${source}-${d.id}`);
                return (
                  <li key={d.id}>
                    <button onClick={() => toggleDoc(d)} data-testid={`docpicker-doc-${d.id}`} className={`w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-left transition-colors ${sel ? 'bg-emerald-50 ring-1 ring-emerald-300' : 'bg-surface ring-1 ring-line/70'}`}>
                      <span className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${sel ? 'bg-emerald-500 text-white' : 'bg-surface-subtle text-ink-muted'}`}>
                        {sel ? <Check size={18} /> : <FileText size={18} />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink truncate">{docName(d)}</p>
                        {d.file_size ? <p className="text-xs text-ink-muted">{(d.file_size / 1024).toFixed(1)} KB</p> : null}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )
        )}
      </div>
    </div>
  );
};

export default MobileCRMDocPicker;
